'use strict';

define('vb/private/stateManagement/rules/rulesEngine',[
  'vb/private/log',
  'vbc/private/logConfig',
  'vb/private/constants',
  'vb/private/stateManagement/rules/ruleSet',
  'vb/private/stateManagement/stateUtils',
  'vb/private/action/assignmentHelper',
  'vb/private/stateManagement/rules/rulesUtils',
], (Log, LogConfig, Constants, RuleSet, StateUtils, AssignmentHelper, RulesUtils) => {
  const logger = Log.getLogger('/vb/stateManagement/rules', [
    // Register custom loggers
    {
      name: 'beforeHandleEvent',
      severity: Constants.Severity.INFO,
      style: LogConfig.FancyStyleByFeature.containerStart,
    },
    {
      name: 'afterHandleEvent',
      severity: Constants.Severity.INFO,
      style: LogConfig.FancyStyleByFeature.containerEnd,
    },
  ]);

  /**
   * For a container all interface constants can either use 'defaultValue' property to configure their values, or use
   * a rules file where the defaultValue for all constants are determined.
   *
   * Today, calculating defaultValue for a constant can come from one or more of the following -
   *
   *   a. "defaultValue" property set on the constant.
   *   b. If the constant is extended by one or more downstream extensions the defaultValue set on those extended
   *   variables can override the base defaultValue as well.
   *
   *  While the value determined from the above, can itself be overridden under these situations,
   *   c. history persisted value: where the history persisted value overrides the defaultValue during init.
   *   d. fromCaller / fromUrl: where the caller / url value changes (anytime?)
   *
   *  a constant value evaluated using 'rules', can use rules defined  either in the base or any of its extensions.
   *  This new behavior only alters (a) and (b)
   *
   *  The RulesEngine is responsible for managing all rules associated to a container and its extended artifacts.
   *  Based on where the rules are defined (base or extension container), an appropriate 'rules context' is used to
   *  execute the rules.
   *
   *  The set of rules associated to a container instance, or each of its extensions, will share the same rules context.
   *
   *  The current behavior of rulesEngine are the following (for 2504):
   *
   *  1. defaultValue expressions must work exactly in rules as it does without. The current behavior of defaultValue
   *     influences the current behavior of rules
   *  2. DT will migrate existing defaultValue expressions into rules 'on demand', so that all existing defaultValue
   *     expressions are turned to rule definitions and have the 'referencedContext' and overlay properties set.
   *
   *  3. Rules are executed from lower to higher, in the following manner -
   *    a. The "condition" set on each rule determines whether it participates in each run.
   *    b. A participating rule, rather than evaluate "overlay" expressions right away, stores the
   *    { expression + context } tuple for each constant. Static values for constants are stored as is.
   *    c. Progressively, a higher order rule participating in the current run overwrites a lower rule's
   *    { expression + context } tuple for a constant if present in its "overlay".
   *      - when a higher order rule has a condition that also requires the value for an interface constant, the last
   *        stored overlay expression for the requested constant is evaluated and its value determined.
   *    d. At the end of the rules engine execution all evaluated values for the constants are returned.
   *    Note: a constant's value is not additive (as in partial updates to constant cannot be accumulated by a
   *    series of rules.). Rather the value of the constant in its entirety, as determined by the highest order
   *    rule is always taken over the lower rule's value.
   *
   *  4. Generally the values of constants from lower order rules will be available to a higher order rule, but within
   *     the same rule if an "overlay" has multiple constants mapping, where the value of one constant depends on
   *     another, then this cannot be guaranteed because overlay is a Map and the order in which constants within the
   *     overlay are evaluated, cannot be guaranteed.
   *  5. Each rulesEngine run is synchronous so at the beginning of the run a snapshot of the current context is used
   *     (in expressions used in rules).
   *
   *  6. At the end of a rulesEngine run all (interface) constant values are updated on the associated scope. Each
   *     update to a constant 'could' fire a valueChanged event that 'might' trigger another rulesEngine run. While this
   *     can quickly become chatty, a future enhancement is planned to consolidate multiple valueChanged events to be
   *     batch delivered to a rulesEngine.
   *
   */
  class RulesEngine {
    /**
     * Constructs the rules engine for the current container's interface constants and all its extended variants. In
     * the example below, all constants from extA and its extensions in the downstream extensions are managed by the
     * same rules engine.
     *
     * - extA/page defines two interface constants - foo and bar and does not have rules
     * |___extA1/page-x extends one constant and defines its own rules
     * |___extA2/page-x does not extend any constant and defines no rules
     * |___extA3/page-x extends both constants and defines rules for both
     *
     * @param container
     * @param {Object} dependencies extension dependencies
     */
    constructor(container, dependencies) {
      this.log = logger;

      this.container = container;

      /**
       * Map of all constants' definition by container extensionId. Extension definitions are fixed up with rules
       * behavior (valueFrom: 'rules'). This property is internal and not publicly configured on the constant itself
       * @type {{}}
       * @private
       */
      this.constantsDef = {};

      this.dependencies = dependencies || {};

      // Map of all defaultValue rules. Anytime we have mixed mode where some extensions have valueFrom: rules
      // configured and other base / parent extension with defaultValue, the rules engine manages evaluation of such
      // constants.
      this.defaultRules = undefined;

      /**
       * Map of rules-based constant expressions that are considered, for extensionId. The value is an
       * array of scope expressions.
       * Example { extA: ["$base.constants.foo", "$constants.foo", "$page.constants.foo"], extB:
       * ["$base.constants.foo"]}
       * @type {{}}
       */
      this.rulesConstantsListByExt = {};

      // an array of all rules based constants that are allowed to be fulfilled by the rulesEngine.
      this.rulesConstants = [];

      /**
       * Map of all rules definitions for the current container and its extensions keyed by extId
       * @type {{}}
       */
      this.rulesDef = {};

      /**
       * Ordered by highest precedence rule to lowest.
       * @type {Array}
       */
      this.orderedRuleSet = [];

      /**
       * Keeps track of all variables in current and outer scope for which a listener is added. When the rules
       * associated to current scope is disposed, all listeners registered by rules on outer containers are removed
       * @type {*[]}
       */
      this.variableChangeTrackerList = [];
    }

    /**
     * Called when container is being disposed. See container.dispose(). Base is called first and then the extensions
     * @param disposed instance being disposed
     */
    dispose(disposed) {
      if (disposed === this.container) {
        this.variableChangeTrackerList.forEach(({ variable, tracker }) => {
          if (tracker) {
            const vCont = variable.scope.container;

            // if variable's container matches the one being disposed, remove the listener. IOW
            // - if variable defined in base and an extension rule referenced it, or
            // - if local variable defined in some extension of base, is referenced by a rule from that extension
            if (vCont.id === disposed.id) {
              // tracker.eventSource is the variable.onValueChanged signal
              tracker.eventSource.remove(tracker.eventListener, this);
            } else if (vCont.id !== this.container.id) {
              // if container being disposed is different from vCont, where the variable is defined, then remove
              // listeners. Example, a page being disposed must remove listeners on application.variable
              const foundIndex = vCont.variablesListeners.findIndex((t) => t === tracker);
              if (foundIndex >= 0) {
                tracker.eventSource.remove(tracker.eventListener, this);
                vCont.variablesListeners.splice(foundIndex, 1);
              }
            }
          }
        });

        this.variableChangeTrackerList = [];

        this.orderedRuleSet.forEach((rs) => {
          rs.dispose();
        });
        this.orderedRuleSet = [];
        if (this.defaultRules) {
          Object.keys(this.defaultRules).forEach((drk) => {
            const dr = this.defaultRules[drk];
            dr.dispose();
          });
        }
        this.defaultRules = undefined;
      }
    }

    /**
     * Getter to retrieve an 'updated' constants def
     * @returns {*}
     */
    getConstantsDef(extId = this.container.extensionId) {
      return this.constantsDef[extId];
    }

    /**
     * Primes the rules engine with the rules definitions from base or child extensions. Also augments the
     * constantsDef or builds a rules constants def for extensions
     * @param container
     * @param {Object} rulesConstantsDef Map of interface constants definitions whose value comes from rules. For
     * extension/constants this will be an empty Object. For base this ideally should be all interface constants' def
     * @param {Object} rulesDef containing the array of rules
     */
    primeWithRules(container = this.container, rulesConstantsDef = {}, rulesDef = {}) {
      const rCS = rulesConstantsDef;
      if (rulesDef.rules && rulesDef.rules.length === 0) {
        this.log.warn('A rules definition was provided that includes no rules. Unable to locate any rule based',
          'constants.');
      }

      if (Object.keys(rulesConstantsDef).length) {
        // all rules based constants are all the interface constants from base.
        Object.keys(rulesConstantsDef).forEach((cName) => {
          rCS[cName][Constants.VariablePropertiesInternal.VALUEFROM] = Constants.VariablePropertyValueFrom.RULES;
        });
      } else if (container.isExtension()) {
        // locate the specific constants extended by the extension container in the overlay
        rulesDef.rules.forEach((rd) => {
          const { overlay } = rd;
          Object.keys(overlay).forEach((ok) => {
            const cName = RulesUtils.constantNameFromExpr(ok);
            rCS[cName] = rCS[cName] || (rulesConstantsDef[cName] || {});
            rCS[cName][Constants.VariablePropertiesInternal.VALUEFROM] = Constants.VariablePropertyValueFrom.RULES;
          });
        });
      }

      // store an array of allowed constants for each extension (empty array if none are allowed)
      this.rulesConstantsListByExt[container.extensionId] = Object.keys(rCS)
        .flatMap((rc) => RulesUtils.exprsFromConstantName(container, rc));

      if (rulesDef.rules && rulesDef.rules.length) {
        this.rulesDef[container.extensionId] = rulesDef.rules;
      }

      this.constantsDef[container.extensionId] = rCS;
    }

    /**
     * called when all interface constants have been created and the rulesScopes / contexts available.
     * @param container
     * @param cdefs interface constants definitions
     */
    setupRuleSets(container = this.container, cdefs = {}) {
      const constantsDefs = cdefs;
      if (constantsDefs && Object.keys(constantsDefs).length) {
        Object.keys(constantsDefs).forEach((cd) => {
          const { rulesScopes = [] } = constantsDefs[cd];
          const topRuleScope = rulesScopes && rulesScopes[0];
          if (topRuleScope && topRuleScope.valueFrom === Constants.VariablePropertyValueFrom.RULES) {
            this.rulesConstants.push(cd);
          }
        });
      }

      // fixup the list of rulesConstantsListByExt, so that we know exactly which ones each extension's rules can
      // provide.
      // store an array of allowed constants for each extension
      Object.entries(this.rulesConstantsListByExt).forEach(([k, exprs]) => {
        const includeExprs = [];
        // drop constants whose value must never be determined by rules. This can happen if a constant is extended
        // and has "defaultValue" configured on the top-most customer extension, but may have rules from lower order
        // extensions. This may be needed in an example like below
        // ExtA: { constants: foo, bar }
        // ExtA has rules:
        //  rule1: {
        //    "condition": "{{ $constants.bar === 'US' }}",
        //    "overlay": {
        //      "$constants.foo": "$"
        //    }
        //  },
        //  rule2: {
        //    "overlay": {
        //      "$constants.bar": "US"
        //    }
        //  }
        //
        // ExtB extends ExtA and overrides bar, defining a defaultValue
        //  {
        //    "extended": {
        //      "constants": {
        //        "bar": { "defaultValue": "JA" }
        //      }
        //    }
        //  }
        //
        // Note: when evaluating rules for ExtA, the value for bar must never be "US" rather the current
        // defaultValue set on ExtB must always be used.
        exprs.forEach((v) => {
          if (this.rulesConstants.includes(RulesUtils.constantNameFromExpr(v))) {
            includeExprs.push(v);
          }
        });
        this.rulesConstantsListByExt[k] = includeExprs;
      });

      if (container === this.container) {
        // get the extensionsArray and reorder rules based on the order
        const orderedRulesDef = this.rulesDef[container.extensionId] || []; // start with this container's rulesDef
        if (orderedRulesDef.length) {
          this.orderedRuleSet.push(new RuleSet(this, container, orderedRulesDef));
        }

        // if container has extensions build an orderedRulesList based on the extension dependency order
        if (container.extensionsArray) {
          container.extensionsArray.forEach((cext) => {
            const extId = cext.extensionId;
            const extRulesDef = this.rulesDef[extId] || [];
            if (extRulesDef.length) {
              const ruleSet = new RuleSet(this, cext, extRulesDef);
              this.orderedRuleSet = [ruleSet].concat(this.orderedRuleSet);
            }
          });
        }
      }

      // setup a 'DefaultValueRules' for all defaultValue property evaluation if we have interleaved rules and
      // defaultValue configured in the extension hierarchy.
      // - extA/page has no rules - uses DefaultValueRule
      // |___extA1/page-x defines rules
      // |___extA2/page-x defines no rules (uses DefaultValueRule)
      // |___extA3/page-x defines rules
      if (Object.keys(constantsDefs).length && this.orderedRuleSet.length) {
        const deps = StateUtils.buildVariablesDependenciesGraph(constantsDefs, container.scopeResolver);
        Object.keys(deps).forEach((c) => {
          const dep = deps[c];
          if (dep.variables && dep.variables.length) {
            const depVarsArr = [];
            dep.variables.forEach((dvar) => {
              // TODO: deal with multiple nested expressions in def
              depVarsArr.push(RulesUtils.locateExprFromVar(dep.expressions.original[0], dvar.variableName));
            });

            constantsDefs[c].dependencies = depVarsArr;
          }
        });
        this.defaultRules = (RuleSet.createDefaultRules(this, constantsDefs)) || {};
      }
    }

    /**
     * For a set of rules (ruleset) associated to each container instance any variable or constant that is in the
     * referencedContext for each rule requires a valueChangeListener added to it. When that variable value changes,
     * this listener is notified of the change and each rule is conditionally checked before re-execution.
     *
     * @param availableContexts for the base where the constant is defined.
     */
    addRulesValueChangeListeners(availableContexts) {
      // only necessary if we have rules at any level
      // gather all referenced variables to add this listener to. A single listener is appended to each unique
      // variable or constant that is referenced on a specific container instance
      const varsList = [];
      if (Object.keys(this.rulesDef).length) {
        this.orderedRuleSet.forEach((ors) => {
          ors.rules.forEach((rule) => {
            (rule.referencedVars || []).forEach((rVar) => {
              const { variable, name, namespace } = RulesUtils.getVariableFromReferenced(rVar, rule.container,
                rule.availableContexts || availableContexts);
              if (!variable) {
                // constants with no expressions are un-locatable using scope.getVariable() call
                const isConstants = namespace === Constants.VariableNamespace.CONSTANTS;
                const logLevelFunc = isConstants ? this.log.finer : this.log.warn;
                const extraMsg = isConstants ? '' : '. Please check your configuration.';
                logLevelFunc('Unable to locate variable or constant using the expression', rVar,
                  'that is referenced in rule', rule.id, extraMsg);
              } else if (!varsList.find((v) => (v.variable === variable))) {
                varsList.push({ variable, name, namespace });
              }
            });
          });
        });

        // add a listener to that container's variable
        this.variableChangeTrackerList = varsList;
        (varsList).forEach((vInfo) => {
          const vCont = vInfo.variable.scope.container;
          this.handleValueChangedListenerForRules(vCont, vInfo);
        });
      }
    }

    /**
     * custom valueChanged listener that conditionally re-executes rules based on the context change
     * @param container container where the variable is defined
     * @param {Object} vInfo with variable, name and namespace properties
     */
    handleValueChangedListenerForRules(container, vInfo) {
      const variableInfo = vInfo;
      const { variable } = variableInfo;
      if (!variable.onValueChanged) {
        return;
      }

      const rulesValueChangeListener = (e) => {
        const eventPayload = e || {};
        // const context = availableContexts.clone();
        // context[Constants.ContextName.EVENT] = e; // capture event payload on the scope
        const eventName = e.type; // vb event type and name is the same
        this.log.info('RulesEngine for container', this.container.id,
          'handling variable event', eventName, 'with payload:', e);
        try {
          // run rules
          this.run(Object.assign({}, eventPayload));
          this.log.info('RulesEngine for container', this.container.id,
            'handled variable event', eventName, 'successfully');
        } catch (error) {
          this.log.error('Failed to handle variable event', eventName, error);
        }
      };

      const varChangeTracker = {
        eventSource: variable.onValueChanged,
        eventListener: rulesValueChangeListener,
      };
      variable.addValueChangedListener(varChangeTracker.eventListener, this, true);
      container.variablesListeners.push(varChangeTracker);
      variableInfo.tracker = varChangeTracker;
    }

    /**
     * Called only during initialization this runs the rulesets in order. Because this is determining the initial
     * values for the rules based constants the context will be undefined for these.
     */
    getInitialValues() {
      if (!this.orderedExtensions) {
        this.orderedExtensions = [];
        this.orderedRuleSet.forEach((ors) => {
          this.orderedExtensions.push(ors.extensionContainer.extensionId);
        });
      }

      this.run();
    }

    /**
     * Evaluates rules both during initialization and when the rules context changes.
     *
     * This returns coerced rule results without writing them to the constants store. Normal rules execution calls run()
     * to write the values back; $withEnv reads use this directly with temporary external contexts.
     *
     * @param {Object} [contextChange] change payload describing the variable/constant that triggered re-evaluation
     * @param {Object} [externalContexts] optional temporary contexts to expose while setting up rule conditions
     * @returns {Object} map of canonical constant expressions to coerced rule values
     */
    evaluate(contextChange, externalContexts) {
      let finalResults = {};
      let continueToRunRules = true;
      let nextIndex = 0;
      let gatherDefaultValuesIfNeeded = false;

      const processResultsFromRuleset = (retVal) => {
        if (typeof retVal === 'object') {
          Object.keys(retVal).forEach((retExpr) => {
            // the returned expression might include expressions in other variants. Use a standard expression -
            // '$base.constants.foo' works universally
            const targetExpr = RulesUtils.getTargetExprFromAnotherExpr(retExpr);
            // Use loose inequality intentionally: `x != null` is false only for null/undefined, so this preserves
            // valid falsy values without using the ES2020 `??` operator. TODO: Logged
            // https://jira.oraclecorp.com/jira/browse/VBS-50342 to take care of the null value as well, leaving it as
            // is for now until we can coordinate with the DT team
            finalResults[targetExpr] = (finalResults[targetExpr] != null) ? finalResults[targetExpr] : retVal[retExpr];
          });
        }
      };

      /*
       * Example...Page Initial Values before vbEnter is like below
       * // | Constant | Ext A  | Ext B  |  ExtC || ExtC Rules | Initial Value |
       * // ---------------------------------------------------------------------
       * // | foo      |   va   |   vb   |       || undefined  |       vb      |
       * // | qux      |   va   |   vb   |       || undefined  |       vb      |
       * // | lux      |   va   |        |       || undefined  |       va[1]   |
       *
       * [1]: since ExtC rules return a invalid value, we fall back on the defaultValue just during init stage
       *
       * Later as part of some variable changing, the rules are re-run so we have,
       *
       * // | Constant | Ext A  | Ext B  |  ExtC || ExtC Rules |    New Value  |
       * // ---------------------------------------------------------------------
       * // | foo      |   va   |   vb   |       || ra         |       ra      |
       * // | qux      |   va   |   vb   |       || rb         |       rb      |
       * // | lux      |   va   |        |       || rc         |       rc      |
       *
       * Again some other variable change causes the rules to re-run, so we have:
       *
       * // | Constant | Ext A  | Ext B  |  ExtC || ExtC Rules | Expected Value |
       * // ---------------------------------------------------------------------
       * // | lux      |   va   |        |       || undefined  |     rc [2]     |
       *
       * [2]: If a rule resets value for lux to undefined, we use the last store value 'rc', over 'va', as undefined is
       * considered equivalent to no change.
      */
      const gatherDefaultValuesForRun = ({ extensionContainer }, fResults, liveRulesState, skipDefaulting = true) => {
        const unfilledDVConstants = this.getUnfilledDVConstants(extensionContainer, fResults, skipDefaulting);
        unfilledDVConstants.forEach((ufdvc) => {
          const defRule = this.defaultRules[ufdvc];
          processResultsFromRuleset(defRule.run(contextChange, liveRulesState));
        });
      };

      // the pre-condition checks each rule from lowest to highest determining whether a rule needs to run. If it
      // participates in the current run, then the overlay assignment is captured, and with each higher
      // participating rule, the overlay is progressively updated. This is intentionally done to mimic the defaultValue
      // config property behavior. Finally, the valid rules are run from highest to lowest with the expression
      // evaluated at this time. The values of constants are always stored and used locally by lower order rules.
      // A context change triggers the rules engine, but ordinary rules must recompute the full active rule snapshot.
      // Passing undefined prevents referencedContext from filtering out still-true rules whose referenced variable did
      // not change in this event. DefaultValueRule still receives the original contextChange above.
      const ruleValues = {};
      const ruleConstants = {};
      const liveRulesSet = [];
      /*
        ruleSet: live set of rules to run
        ruleConstants: map of constants with their expressions
        ruleValues: map of constants and their rule evaluated values
        externalContexts: optional temporary contexts used by condition expressions
       */
      const liveRulesState = {
        ruleSet: liveRulesSet,
        ruleConstants,
        ruleValues,
        externalContexts,
      };
      const reversed = this.orderedRuleSet.slice().reverse();
      reversed.forEach((rors) => {
        // what gets returned is the list of constants and their resolved values or expression. At this point the
        // value of a constant is not determined
        const retVal = rors.prepareRun(undefined, liveRulesState);
        if (typeof retVal === 'object') {
          Object.keys(retVal).forEach((rV) => {
            // the returned expression might include expressions in other variants. Use a standard form -
            // '$base.constants.foo' works universally. Also overwrite with the higher order expression
            const targetExpr = RulesUtils.getTargetExprFromAnotherExpr(rV);
            ruleConstants[targetExpr] = retVal[rV];
            ruleValues[targetExpr] = undefined; // only stores values when getter is called
          });
        }
      });

      // run rules using just the rules that are active/needed for this run, in the priority order
      for (nextIndex = 0; continueToRunRules && nextIndex < liveRulesSet.length; nextIndex++) {
        const ruleset = liveRulesSet[nextIndex];
        if (gatherDefaultValuesIfNeeded) {
          gatherDefaultValuesIfNeeded = false;

          gatherDefaultValuesForRun(ruleset, finalResults, liveRulesState);

          // after gathering default values check if all constant's values are filled or if rules are to continue
          continueToRunRules = this.continueRulesRun(finalResults);
        }

        if (continueToRunRules) {
          const retVal = ruleset.run(undefined, liveRulesState);

          // at the end of each ruleset, check if we need to run the next ruleset. If not, bail.
          // If yes, before proceeding to the next ruleset, check if the unresolved constants have defaultValue set.
          // If it does, then use it to gather the values for those before running the next ruleset.
          if (typeof retVal === 'object') {
            processResultsFromRuleset(retVal);

            // filter out constants that don't use rules. This is just in case results return extra
            finalResults = Object.fromEntries(Object.entries(finalResults)
              .filter((fr) => this.rulesConstants.includes(RulesUtils.constantNameFromExpr(fr[0]))));

            continueToRunRules = this.continueRulesRun(finalResults);
            gatherDefaultValuesIfNeeded = !!(continueToRunRules);
          }
        }
      }

      // ran through all rulesets, but we still are missing values for some constants! pick up defaults; Only do
      // this when getting initial values
      if (gatherDefaultValuesIfNeeded) {
        gatherDefaultValuesForRun({ extensionContainer: this.container }, finalResults, liveRulesState,
          !!contextChange);
      }

      // evaluate() is shared by normal rules write-back and $withEnv reads, so coerce here instead of only in run().
      // This keeps values produced with extra contexts consistent with values written to the normal constants store.
      const coercedResults = {};
      Object.keys(finalResults).forEach((expr) => {
        const name = RulesUtils.constantNameFromExpr(expr);
        const variable = this.container.scope.getVariable(name, Constants.VariableNamespace.CONSTANTS);
        coercedResults[expr] = this.coerceToType(name, finalResults[expr], variable.type);
      });
      return coercedResults;
    }

    /**
     * Executes the rules engine both during initialization and when the rules context changes. During
     * initialization the rules context is undefined. If the rules returns new values for the interface constants
     * they are set by this method.
     *
     * @param {Object} [contextChange] change payload describing the variable/constant that triggered re-evaluation
     */
    run(contextChange) {
      const finalResults = this.evaluate(contextChange);

      // we now have all final rules values. Update the variables
      Object.keys(finalResults).forEach((expr) => {
        const name = RulesUtils.constantNameFromExpr(expr);
        const variable = this.container.scope.getVariable(name, Constants.VariableNamespace.CONSTANTS);
        variable.setValueInternal(finalResults[expr], { valueFrom: 'rules' });
      });
    }

    /**
     * Coerces a rules value to conform to the type set on the constant
     * @param {string} name of variable
     * @param {any} value
     * @param {any} type
     * @returns {*}
     */
    coerceToType(name, value, type) {
      // pick attributes from the return value into an object that matches the type
      const resolvedType = StateUtils.getType(name, { type }, this.container.scopeResolver);
      const newValue = AssignmentHelper.coerceType(value, resolvedType);
      this.log.finer('The rules value', value, 'for the constant', name, 'was coerced to', newValue);
      return newValue;
    }

    /**
     * Returns all rule values produced with additional expression contexts.
     *
     * @param {Object} externalContexts extra contexts to expose while evaluating rules
     * @returns {Object|undefined} rule values, or undefined when no external-context evaluation applies
     */
    getConstantValuesForExternalContexts(externalContexts) {
      return externalContexts ? this.evaluate(undefined, externalContexts) : undefined;
    }

    /**
     * Returns one rules-based constant value from rule results produced with additional expression contexts.
     *
     * @param {Object} variable rules-based constant variable instance
     * @param {Object} externalContexts extra contexts supplied to rules evaluation
     * @param {Object} ruleResults rule results produced with external contexts
     * @returns {{hasValue: boolean, value: *}|undefined} external-context result record, or undefined when no
     *  external-context evaluation applies
     */
    getConstantValueForExternalContexts(variable, externalContexts, ruleResults) {
      const results = ruleResults || this.getConstantValuesForExternalContexts(externalContexts);
      if (!results) {
        return undefined;
      }

      const rulesVariable = /** @type {any} */ (variable);
      // Rules results are keyed by constant expression, so derive the current constant's lookup key once.
      const expr = RulesUtils.getTargetExprFromAnotherExpr(`$constants.${rulesVariable.name}`);

      // Return a result record even when this run did not produce a value for the current constant. $withEnv callers
      // use the record to avoid falling back to a stale normal rules value after external-context evaluation ran.
      if (Object.prototype.hasOwnProperty.call(results, expr)) {
        return { hasValue: true, value: results[expr] };
      }

      return { hasValue: false, value: undefined };
    }

    /**
     * compare the list of constants that have been filled, against all constants that need to be filled.
     * @param filled
     * @param all
     * @returns {false|*} if no more ruleset/rules need to run.
     */
    continueRulesRun(filled = {}, all = this.rulesConstants || []) {
      const filledKeys = Object.keys(filled);
      const hasKeyMatch = (k) => all.includes(RulesUtils.constantNameFromExpr(k));
      // Preserve existing rules behavior: only undefined is unfilled; null and falsy values are valid rule results.
      const hasValueFilled = (k) => hasKeyMatch(k) && filled[k] !== undefined;
      return !(filledKeys.length === all.length && filledKeys.every(hasValueFilled));
    }

    /**
     * Returns an array of unfilled constants
     * @param forExtension
     * @param filled
     * @returns {string[]}
     * @private
     */
    getUnfilledDVConstants(forExtension, filled = {}, skipDefaulting = true) {
      // an extension's rules can fill the value for a constant either from rules, or from a defaultValue if it is
      // configured. if neither are configured then it can skip it entirely.
      //
      const fKeys = Object.keys(filled);
      const { constantsDef = {}, rulesConstants: all = [] } = this;
      const fKeysConstants = (fKeys.map((fk) => RulesUtils.constantNameFromExpr(fk))) || [];
      const unfilledConstants = all.filter((ak) => !fKeysConstants.includes(ak));
      const dVConstantsForExtension = [];

      if (unfilledConstants) {
        // check if any of these constants have defaultValue set
        const cdefs = constantsDef[this.container.extensionId];
        if (cdefs) {
          unfilledConstants.forEach((ufc) => {
            if (cdefs) {
              const { rulesScopes } = cdefs[ufc];
              const dv = rulesScopes.find((rs) => rs.extension === forExtension
                && rs.valueFrom === Constants.VariablePropertyValueFrom.INLINE);
              if (dv) {
                dVConstantsForExtension.push(ufc);
              }
            }
          });
        } else {
          // if we have un filled constants just try to fill them anyway
          return !skipDefaulting ? unfilledConstants : [];
        }
      }

      return dVConstantsForExtension;
    }
  }

  return RulesEngine;
});

